import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { Link } from "wouter";

import { Flag } from "@/components/bits";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { publicApi } from "@/services/api-client";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import {
  availablePaymentMethods,
  extractServices,
  isCryptoPaymentService,
  type ServiceCatalog,
} from "@/lib/service-catalog";
import { METHOD_LABEL, countryOf, fmt, type Method } from "@/types/dashboard";
import {
  ErrorBox,
  KV,
  Tag,
  normalizeAmount,
  record,
  rows,
  safeUrl,
  text,
  useFinanceGate,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";
import {
  OperationLookup,
  type LookupKind,
} from "@/features/finance/operation-lookup";

import { useDashboard, type OperationInput } from "./dashboard-context";

export type PaymentKind = OperationInput["kind"];

const TITLES: Record<PaymentKind, string> = {
  DEPOSIT: "Effectuer un dépôt",
  TRANSFER: "Envoyer à un utilisateur BizConnectPay",
  WITHDRAWAL: "Effectuer un retrait",
};
const ROUTE: Record<PaymentKind, string> = {
  DEPOSIT: "/payment-intents",
  WITHDRAWAL: "/withdrawals",
  TRANSFER: "/wallet-transfers",
};

export function PaymentDialog({
  kind,
  onClose,
  defaultWalletId,
}: {
  kind: PaymentKind | null;
  onClose: () => void;
  defaultWalletId?: string;
}) {
  const { wallets, profile, refresh } = useDashboard();
  const gate = useFinanceGate();
  const services = useServer<ServiceCatalog>(
    kind && kind !== "TRANSFER" && gate.allowed ? "/dashboard/services" : null,
  );
  const externalAccountsRes = useServer<unknown>(
    kind && kind !== "TRANSFER" && gate.allowed ? "/external-accounts" : null,
  );
  const [feePayer, setFeePayer] = useState<"MERCHANT" | "CUSTOMER">("MERCHANT");
  const externalAccounts = useMemo(
    () => rows(externalAccountsRes.data).map(record),
    [externalAccountsRes.data],
  );
  const [selectedExternalId, setSelectedExternalId] =
    useState<string>("manual");
  const active = useMemo(
    () => wallets.filter((w) => w.status === "ACTIVE"),
    [wallets],
  );
  const idem = useIdempotencyKey();
  const [walletId, setWalletId] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<Method>("");
  const [otpCode, setOtpCode] = useState("");
  const [target, setTarget] = useState("");
  const [description, setDescription] = useState("");
  const [cryptoEmail, setCryptoEmail] = useState("");
  const [cryptoFirstName, setCryptoFirstName] = useState("");
  const [cryptoLastName, setCryptoLastName] = useState("");
  const [cryptoReturnUrl, setCryptoReturnUrl] = useState("");
  const [cryptoMemo, setCryptoMemo] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState<unknown>(null);
  const [step, setStep] = useState<"form" | "review" | "done">("form");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!kind) return;
    const list = activeRef.current;
    const wallet = list.find((w) => w.id === defaultWalletId) ?? list[0];
    setWalletId(wallet?.id ?? "");
    setAmount("");
    setOtpCode("");
    setTarget("");
    setSelectedExternalId("manual");
    setFeePayer("MERCHANT");
    setDescription("");
    setCryptoEmail("");
    setCryptoFirstName("");
    setCryptoLastName("");
    setCryptoReturnUrl("");
    setCryptoMemo("");
    setErrors({});
    setRequestError(null);
    setResult(null);
    setStep("form");
    idem.reset();
    setMethod(kind === "TRANSFER" ? "internal" : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultWalletId, kind]);

  const wallet = wallets.find((w) => w.id === walletId);
  const methods = useMemo(
    () =>
      kind === "TRANSFER"
        ? ["internal"]
        : wallet
          ? availablePaymentMethods(
              services.data,
              wallet.country,
              wallet.currency,
              kind === "DEPOSIT" ? "DEPOSIT" : "WITHDRAWAL",
            )
          : [],
    [wallet, kind, services.data],
  );

  const selectedService = useMemo(() => {
    if (!wallet || !kind || kind === "TRANSFER" || !method) return undefined;
    const operations =
      kind === "DEPOSIT"
        ? ["DEPOSIT", "COLLECTION", "PAYIN"]
        : ["WITHDRAWAL", "DISTRIBUTION", "PAYOUT"];
    return extractServices(services.data).find(
      (service) =>
        service.available !== false &&
        service.country?.code.toUpperCase() === wallet.country.toUpperCase() &&
        service.currency?.code.toUpperCase() ===
          wallet.currency.toUpperCase() &&
        operations.includes(service.operation.toUpperCase()) &&
        service.payment_method.toLowerCase() === method.toLowerCase(),
    );
  }, [wallet, kind, method, services.data]);
  const isCrypto =
    kind !== "TRANSFER" &&
    Boolean(selectedService && isCryptoPaymentService(selectedService));
  const feeQuery =
    wallet && kind && kind !== "TRANSFER" && method
      ? `/dashboard/fees?country=${encodeURIComponent(wallet.country)}&currency=${encodeURIComponent(wallet.currency)}&payment_method=${encodeURIComponent(method)}`
      : null;
  const feesRes = useServer<unknown>(gate.allowed ? feeQuery : null);
  const feesData = useMemo(() => record(feesRes.data), [feesRes.data]);
  const collectionFeePct = Number(
    text(feesData, "collection_fee_percentage", "collection.percentage") ||
      "4.5",
  );
  const distributionFeePct = Number(
    text(feesData, "distribution_fee_percentage", "distribution.percentage") ||
      "3",
  );

  const otpRequired = useMemo(() => {
    if (kind !== "DEPOSIT" || !wallet || !method) return false;
    const operationNames = ["DEPOSIT", "COLLECTION", "PAYIN"];
    return extractServices(services.data).some(
      (service) =>
        service.available !== false &&
        service.country?.code.toUpperCase() === wallet.country.toUpperCase() &&
        service.currency?.code.toUpperCase() ===
          wallet.currency.toUpperCase() &&
        operationNames.includes(service.operation.toUpperCase()) &&
        service.payment_method.toLowerCase() === method.toLowerCase() &&
        service.otp_required === true,
    );
  }, [kind, wallet, method, services.data]);

  useEffect(() => {
    if (!methods.includes(method)) setMethod(methods[0] ?? "");
  }, [methods, method]);

  function pickWallet(id: string) {
    setWalletId(id);
    if (kind !== "TRANSFER") setMethod("");
  }

  /** Exact contract payloads. */
  function payload(): Record<string, unknown> | null {
    const value = normalizeAmount(amount);
    if (!wallet || !kind || !value) return null;
    const desc = description.trim();
    if (kind === "DEPOSIT")
      return {
        amount: value,
        currency: wallet.currency,
        country: wallet.country,
        operation: "DEPOSIT",
        payment_method: method,
        fee_payer: feePayer,
        payer: {
          lang: profile.language || "fr",
          ...(isCrypto
            ? {
                ...(cryptoEmail.trim() ? { email: cryptoEmail.trim() } : {}),
                ...(cryptoFirstName.trim()
                  ? { first_name: cryptoFirstName.trim() }
                  : {}),
                ...(cryptoLastName.trim()
                  ? { last_name: cryptoLastName.trim() }
                  : {}),
                ...(cryptoReturnUrl.trim()
                  ? { return_url: cryptoReturnUrl.trim() }
                  : {}),
              }
            : { phone_number: target.trim() }),
          ...(!isCrypto && otpRequired && otpCode.trim()
            ? { otp_code: otpCode.trim() }
            : {}),
        },
        ...(isCrypto
          ? {
              metadata: {
                ...(selectedService?.accepted_coins?.length
                  ? { accepted_coins: selectedService.accepted_coins }
                  : {}),
              },
            }
          : {}),
        description: desc,
      };
    if (kind === "WITHDRAWAL")
      return {
        wallet_id: wallet.id,
        amount: value,
        currency: wallet.currency,
        country: wallet.country,
        payment_method: method,
        destination: target.trim(),
        metadata: {
          ...(desc ? { description: desc } : {}),
          ...(isCrypto
            ? {
                asset_code: selectedService?.crypto_asset_code || method,
                amount_type: "fiat",
                ...(cryptoMemo.trim()
                  ? { destination_memo: cryptoMemo.trim() }
                  : {}),
              }
            : {}),
        },
      };
    return {
      source_wallet_id: wallet.id,
      recipient_matricule: target.trim().toUpperCase(),
      amount: value,
      currency: wallet.currency,
      description: desc,
    };
  }

  const feeBreakdown = useMemo(() => {
    const val = Number(normalizeAmount(amount)) || 0;
    if (
      !wallet ||
      val <= 0 ||
      !kind ||
      kind === "TRANSFER" ||
      feesRes.loading ||
      feesRes.error
    )
      return null;
    const isZero = ["XAF", "XOF", "GNF", "RWF", "UGX"].includes(
      wallet.currency,
    );
    const round = (v: number) =>
      isZero ? Math.round(v) : Math.round(v * 100) / 100;
    if (kind === "DEPOSIT") {
      const fee = round((val * collectionFeePct) / 100);
      if (feePayer === "CUSTOMER") {
        return {
          charged: round(val + fee),
          fee,
          net: round(val),
          rate: collectionFeePct,
        };
      }
      return {
        charged: round(val),
        fee,
        net: Math.max(0, round(val - fee)),
        rate: collectionFeePct,
      };
    }
    const fee = round((val * distributionFeePct) / 100);
    return {
      payout: round(val),
      fee,
      totalDebit: round(val + fee),
      rate: distributionFeePct,
    };
  }, [
    amount,
    wallet,
    kind,
    collectionFeePct,
    distributionFeePct,
    feePayer,
    feesRes.loading,
    feesRes.error,
  ]);

  function review(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next: Record<string, string> = {};
    const value = normalizeAmount(amount);
    if (!wallet) next.wallet = "Choisissez un wallet actif.";
    if (isCrypto && wallet?.currency.toUpperCase() !== "USD")
      next.wallet = "Les opérations crypto nécessitent un wallet USD actif.";
    if (
      kind !== "TRANSFER" &&
      (services.loading || services.error || !methods.includes(method))
    )
      next.method = "Choisissez une méthode disponible dans le catalogue API.";
    if (!value)
      next.amount = "Saisissez un montant décimal positif (ex. 5000 ou 12.50).";
    else if (wallet && kind !== "DEPOSIT") {
      const requiredBalance =
        kind === "WITHDRAWAL" && feeBreakdown?.totalDebit
          ? feeBreakdown.totalDebit
          : Number(value);
      if (requiredBalance > wallet.balance) {
        next.amount = `Solde insuffisant (${fmt(wallet.balance, wallet.currency)} disponible, ${fmt(requiredBalance, wallet.currency)} requis avec frais).`;
      }
    }
    if (
      kind === "TRANSFER" &&
      !/^BCP[A-Z0-9]{8,18}$/.test(target.trim().toUpperCase())
    )
      next.target = "Saisissez un matricule BizConnectPay valide (BCP...).";
    if (kind !== "TRANSFER" && isCrypto && target.trim().length < 3)
      next.target = "Saisissez une adresse crypto valide.";
    else if (
      kind !== "TRANSFER" &&
      !isCrypto &&
      target.replace(/\D/g, "").length < 8
    )
      next.target = "Saisissez un numéro de téléphone valide.";
    if (
      kind === "DEPOSIT" &&
      isCrypto &&
      cryptoEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cryptoEmail)
    )
      next.cryptoEmail = "Saisissez une adresse e-mail valide.";
    if (
      kind === "DEPOSIT" &&
      isCrypto &&
      cryptoReturnUrl &&
      !/^https:\/\//i.test(cryptoReturnUrl)
    )
      next.cryptoReturnUrl = "Utilisez une URL HTTPS valide.";
    if (otpRequired && !otpCode.trim())
      next.otp = "Saisissez le code OTP demandé par votre opérateur.";
    setErrors(next);
    if (!Object.keys(next).length) {
      setRequestError(null);
      setStep("review");
    }
  }

  async function confirm() {
    const body = payload();
    if (!body || !kind) return;
    if (
      kind !== "TRANSFER" &&
      (!gate.allowed ||
        services.loading ||
        services.error ||
        !methods.includes(method))
    ) {
      setRequestError(
        new Error(
          "La méthode de paiement n’est plus disponible. Rechargez le catalogue et vérifiez votre saisie.",
        ),
      );
      return;
    }
    // Same key while the draft is unchanged: an uncertain retry cannot double-charge.
    const key = idem.forPayload({ kind, body });
    setSubmitting(true);
    setRequestError(null);
    try {
      const { data } = await publicApi<Record<string, unknown>>(ROUTE[kind], {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body,
      });
      setResult(record(data));
      setStep("done");
      idem.reset();
      void refresh();
    } catch (caught) {
      setRequestError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const targetLabel =
    kind === "TRANSFER"
      ? "Matricule du destinataire"
      : kind === "WITHDRAWAL"
        ? isCrypto
          ? "Adresse blockchain du bénéficiaire"
          : "Numéro de destination"
        : isCrypto
          ? "Numéro du payeur (non requis pour la crypto)"
          : "Numéro du payeur";
  const country = wallet ? countryOf(wallet.country) : null;
  const providerLink = safeUrl(
    result &&
      (result.provider_link ?? record(result.payment_intent).provider_link),
  );
  const ref = result
    ? text(result, "reference", "id", "payment_intent.id", "transfer.reference")
    : "";
  const lookupKind: LookupKind | null =
    kind === "DEPOSIT"
      ? "payment-intents"
      : kind === "WITHDRAWAL"
        ? "withdrawals"
        : null;

  return (
    <Dialog
      open={Boolean(kind)}
      onOpenChange={(open) => !open && !submitting && onClose()}
    >
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-[480px]">
        {kind && !gate.allowed ? (
          <div
            className="flex flex-col items-start gap-3"
            data-testid="payment-gated"
          >
            <DialogTitle>{TITLES[kind]}</DialogTitle>
            <DialogDescription>
              Les opérations financières sont indisponibles tant que le compte
              n'est pas validé.
            </DialogDescription>
            <Link
              href="/verification"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
            >
              <ShieldAlert className="h-3.5 w-3.5" /> Aller à la vérification
            </Link>
          </div>
        ) : step === "done" && result && kind ? (
          <div className="flex flex-col gap-3" data-testid="payment-success">
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="h-6 w-6" />
              </span>
              <DialogTitle>Demande acceptée.</DialogTitle>
              <DialogDescription>
                {ref && (
                  <span className="font-mono text-foreground">{ref}</span>
                )}{" "}
                Statut retourné :{" "}
              </DialogDescription>
              <Tag value={text(result, "status", "state") || "inconnu"} />
            </div>
            {providerLink && (
              <a
                href={providerLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="link-provider"
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-accent px-3 py-2 text-sm font-semibold text-primary"
              >
                Finaliser chez le fournisseur{" "}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}

            {lookupKind && ref && (
              <div className="rounded-lg border p-3">
                <p className="mb-2 text-xs font-semibold">
                  Suivi manuel de l'état
                </p>
                <OperationLookup
                  compact
                  initialKind={lookupKind}
                  initialId={text(
                    result,
                    "id",
                    "reference",
                    "payment_intent.id",
                  )}
                />
              </div>
            )}
            <Button onClick={onClose}>Fermer</Button>
          </div>
        ) : kind && step === "review" && wallet ? (
          <div data-testid="payment-review">
            <DialogHeader>
              <DialogTitle>
                Confirmer : {TITLES[kind].toLowerCase()}
              </DialogTitle>
              <DialogDescription>
                Rien n'est envoyé avant votre confirmation. Une clé
                d'idempotence stable protège ce brouillon contre les doublons.
              </DialogDescription>
            </DialogHeader>
            <div className="my-4 rounded-lg bg-accent/60 p-4 text-center">
              <p className="tnum text-2xl font-extrabold">
                {normalizeAmount(amount)} {wallet.currency}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {country?.name} ·{" "}
                {kind === "TRANSFER"
                  ? "interne"
                  : selectedService?.provider_name ||
                    METHOD_LABEL[method] ||
                    method}
              </p>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">{targetLabel}</dt>
              <dd className="text-right font-mono">
                {kind === "DEPOSIT" && isCrypto
                  ? cryptoEmail || "Lien de paiement crypto"
                  : kind === "TRANSFER"
                    ? target.trim().toUpperCase()
                    : target.trim()}
              </dd>
              <dt className="text-muted-foreground">Libellé</dt>
              <dd className="text-right">{description.trim() || "—"}</dd>
              {kind === "DEPOSIT" && feeBreakdown && (
                <>
                  <dt className="text-muted-foreground">
                    Frais COLLECTION ({feeBreakdown.rate}%)
                  </dt>
                  <dd className="text-right font-mono text-amber-700">
                    {fmt(feeBreakdown.fee, wallet.currency)}
                  </dd>
                  <dt className="font-semibold text-foreground">
                    Crédité au wallet
                  </dt>
                  <dd className="text-right font-mono font-bold text-emerald-700">
                    {fmt(feeBreakdown.net ?? 0, wallet.currency)}
                  </dd>
                </>
              )}
              {kind === "WITHDRAWAL" && feeBreakdown && (
                <>
                  <dt className="text-muted-foreground">
                    Reçu par le bénéficiaire
                  </dt>
                  <dd className="text-right font-mono font-semibold text-emerald-700">
                    {fmt(feeBreakdown.payout ?? 0, wallet.currency)}
                  </dd>
                  <dt className="text-muted-foreground">
                    Frais DISTRIBUTION ({feeBreakdown.rate}% · Mode B)
                  </dt>
                  <dd className="text-right font-mono text-amber-700">
                    + {fmt(feeBreakdown.fee, wallet.currency)}
                  </dd>
                  <dt className="font-semibold text-foreground">
                    Débit total sur le wallet
                  </dt>
                  <dd className="text-right font-mono font-bold">
                    {fmt(feeBreakdown.totalDebit ?? 0, wallet.currency)}
                  </dd>
                </>
              )}
            </dl>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Les frais éventuels sont calculés et retournés par le serveur.
            </p>
            <ErrorBox
              className="mt-3"
              error={requestError}
              onRetry={() => void confirm()}
            />
            <DialogFooter className="mt-5">
              <Button
                variant="outline"
                onClick={() => setStep("form")}
                disabled={submitting}
              >
                <ArrowLeft className="mr-1 h-4 w-4" /> Modifier
              </Button>
              <Button
                onClick={() => void confirm()}
                disabled={submitting}
                data-testid="button-payment-confirm"
              >
                {submitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Confirmer et envoyer
              </Button>
            </DialogFooter>
          </div>
        ) : kind ? (
          <form onSubmit={review} noValidate>
            <DialogHeader>
              <DialogTitle>{TITLES[kind]}</DialogTitle>
              <DialogDescription>
                {kind === "TRANSFER"
                  ? "Le destinataire doit avoir un wallet actif dans le même pays et la même devise."
                  : "Chaque wallet reste dans sa devise d'origine."}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 grid gap-4">
              {active.length === 0 && (
                <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
                  Aucun wallet actif. Les wallets en attente d'accès pays ne
                  peuvent pas opérer.
                </p>
              )}
              {wallet &&
                wallet.currency.toUpperCase() !== "USD" &&
                extractServices(services.data).some(
                  (service) =>
                    service.available !== false &&
                    isCryptoPaymentService(service) &&
                    (kind === "DEPOSIT"
                      ? ["DEPOSIT", "COLLECTION", "PAYIN"]
                      : ["WITHDRAWAL", "DISTRIBUTION", "PAYOUT"]
                    ).includes(service.operation.toUpperCase()),
                ) && (
                  <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                    Les opérations crypto nécessitent un wallet USD actif.
                    Sélectionnez votre wallet USD pour afficher ces méthodes.
                  </p>
                )}
              <div className="grid gap-1.5">
                <Label htmlFor="payment-wallet">
                  Wallet {kind === "DEPOSIT" ? "à créditer" : "source"}
                </Label>
                <Select value={walletId} onValueChange={pickWallet}>
                  <SelectTrigger
                    id="payment-wallet"
                    data-testid="select-payment-wallet"
                  >
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent>
                    {active.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        <span className="flex items-center gap-2">
                          <Flag code={w.country} />
                          {countryOf(w.country).name} · {w.type} ·{" "}
                          {fmt(w.balance, w.currency)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wallet && (
                  <p className="text-xs text-destructive">{errors.wallet}</p>
                )}
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="payment-amount">Montant</Label>
                  <Input
                    id="payment-amount"
                    data-testid="input-payment-amount"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    aria-invalid={Boolean(errors.amount)}
                    placeholder="0"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label>Devise</Label>
                  <div className="flex h-9 items-center rounded-md border bg-muted px-3 font-mono text-sm">
                    {wallet?.currency ?? "—"}
                  </div>
                </div>
                {errors.amount && (
                  <p className="col-span-2 -mt-1.5 text-xs text-destructive">
                    {errors.amount}
                  </p>
                )}
              </div>
              {kind === "DEPOSIT" && (
                <div className="grid gap-1.5">
                  <Label htmlFor="payment-fee-payer">
                    Prise en charge des frais ({collectionFeePct}%)
                  </Label>
                  <Select
                    value={feePayer}
                    onValueChange={(v) =>
                      setFeePayer(v as "MERCHANT" | "CUSTOMER")
                    }
                  >
                    <SelectTrigger
                      id="payment-fee-payer"
                      data-testid="select-payment-fee-payer"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MERCHANT">
                        Marchand — Frais déduits du montant reçu
                      </SelectItem>
                      <SelectItem value="CUSTOMER">
                        Client — Frais ajoutés au montant demandé au payeur
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {wallet && feeBreakdown && (
                <div
                  className="rounded-lg border bg-accent/40 p-3 text-xs space-y-1"
                  data-testid="payment-fee-breakdown"
                >
                  {kind === "DEPOSIT" ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Facturé au payeur :
                        </span>
                        <span className="font-mono font-semibold">
                          {fmt(feeBreakdown.charged ?? 0, wallet.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Frais COLLECTION ({feeBreakdown.rate}%) :
                        </span>
                        <span className="font-mono text-amber-700">
                          {fmt(feeBreakdown.fee, wallet.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-1 font-semibold">
                        <span>Crédité sur le wallet :</span>
                        <span className="font-mono text-emerald-700">
                          {fmt(feeBreakdown.net ?? 0, wallet.currency)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Reçu par le bénéficiaire :
                        </span>
                        <span className="font-mono font-semibold text-emerald-700">
                          {fmt(feeBreakdown.payout ?? 0, wallet.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Frais DISTRIBUTION ({feeBreakdown.rate}% · Mode B) :
                        </span>
                        <span className="font-mono text-amber-700">
                          + {fmt(feeBreakdown.fee, wallet.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-1 font-semibold">
                        <span>Débit total sur le wallet :</span>
                        <span className="font-mono">
                          {fmt(feeBreakdown.totalDebit ?? 0, wallet.currency)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
              {kind !== "TRANSFER" && externalAccounts.length > 0 && (
                <div className="grid gap-1.5">
                  <Label htmlFor="payment-external-account">
                    Compte externe (enregistré ou saisie manuelle)
                  </Label>
                  <Select
                    value={selectedExternalId}
                    onValueChange={(val) => {
                      setSelectedExternalId(val);
                      if (val === "manual") return;
                      const found = externalAccounts.find(
                        (item) => String(item.id) === val,
                      );
                      if (!found) return;
                      setTarget(text(found, "account_number"));
                      const prov = text(found, "provider").toLowerCase();
                      const matchMethod = methods.find(
                        (m) =>
                          m.toLowerCase() === prov ||
                          (METHOD_LABEL[m] ?? "").toLowerCase() === prov ||
                          (prov.includes("orange") &&
                            m.toLowerCase().includes("orange")) ||
                          (prov.includes("mtn") &&
                            (m.toLowerCase().includes("mtn") ||
                              m.toLowerCase().includes("mobile_money"))) ||
                          (prov.includes("wave") &&
                            m.toLowerCase().includes("wave")),
                      );
                      if (matchMethod) setMethod(matchMethod);
                    }}
                  >
                    <SelectTrigger
                      id="payment-external-account"
                      data-testid="select-payment-external-account"
                    >
                      <SelectValue placeholder="Saisie manuelle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">
                        Saisir un numéro manuellement
                      </SelectItem>
                      {externalAccounts.map((acc) => {
                        const accId = String(acc.id);
                        const label =
                          text(acc, "label") ||
                          text(acc, "provider") ||
                          text(acc, "type");
                        const num = text(acc, "account_number");
                        const isDef = Boolean(acc.is_default);
                        return (
                          <SelectItem key={accId} value={accId}>
                            {label} — {num}
                            {isDef ? " (Par défaut)" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {kind !== "TRANSFER" && (
                <div className="grid gap-1.5">
                  <Label htmlFor="payment-method">Méthode</Label>
                  <Select
                    value={method}
                    onValueChange={(value) => {
                      setMethod(value);
                      setOtpCode("");
                      setErrors((current) => ({ ...current, otp: "" }));
                    }}
                    disabled={methods.length < 2}
                  >
                    <SelectTrigger id="payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {methods.map((m) => {
                        const serviceLabel = extractServices(
                          services.data,
                        ).find(
                          (service) =>
                            service.payment_method.toLowerCase() ===
                              m.toLowerCase() &&
                            service.country?.code.toUpperCase() ===
                              wallet?.country.toUpperCase() &&
                            service.currency?.code.toUpperCase() ===
                              wallet?.currency.toUpperCase(),
                        )?.provider_name;
                        return (
                          <SelectItem key={m} value={m}>
                            {isCrypto && selectedService?.crypto_asset_code
                              ? selectedService.crypto_asset_code
                              : serviceLabel || METHOD_LABEL[m] || m}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {services.loading && (
                    <p role="status" className="text-xs text-muted-foreground">
                      Chargement des méthodes disponibles…
                    </p>
                  )}
                  {Boolean(services.error) && (
                    <ErrorBox
                      error={services.error}
                      onRetry={services.reload}
                    />
                  )}
                  {!services.loading && !services.error && !methods.length && (
                    <p className="text-xs text-muted-foreground">
                      Aucun moyen de paiement disponible pour ce pays et cette
                      devise.
                    </p>
                  )}
                  {errors.method && (
                    <p className="text-xs text-destructive">{errors.method}</p>
                  )}
                </div>
              )}
              {kind === "DEPOSIT" && isCrypto && (
                <div className="grid gap-3 rounded-lg border bg-accent/30 p-3">
                  <p className="text-sm font-semibold">Paiement en crypto</p>
                  <p className="text-xs text-muted-foreground">
                    Le client recevra un lien de paiement. Aucun numéro mobile
                    n’est requis.
                  </p>
                  <div className="grid gap-1.5">
                    <Label>Cryptomonnaies disponibles</Label>
                    <p className="rounded-md border bg-background px-3 py-2 text-sm">
                      {(selectedService?.accepted_coins ?? []).join(", ") ||
                        "Actifs définis par la configuration des services"}
                    </p>
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="crypto-email">
                      E-mail du payeur (facultatif)
                    </Label>
                    <Input
                      id="crypto-email"
                      type="email"
                      value={cryptoEmail}
                      onChange={(event) => setCryptoEmail(event.target.value)}
                      aria-invalid={Boolean(errors.cryptoEmail)}
                    />
                    {errors.cryptoEmail && (
                      <p className="text-xs text-destructive">
                        {errors.cryptoEmail}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5">
                      <Label htmlFor="crypto-first-name">Prénom</Label>
                      <Input
                        id="crypto-first-name"
                        value={cryptoFirstName}
                        onChange={(event) =>
                          setCryptoFirstName(event.target.value)
                        }
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="crypto-last-name">Nom</Label>
                      <Input
                        id="crypto-last-name"
                        value={cryptoLastName}
                        onChange={(event) =>
                          setCryptoLastName(event.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="crypto-return-url">
                      URL de retour après paiement (facultatif)
                    </Label>
                    <Input
                      id="crypto-return-url"
                      type="url"
                      value={cryptoReturnUrl}
                      onChange={(event) =>
                        setCryptoReturnUrl(event.target.value)
                      }
                      aria-invalid={Boolean(errors.cryptoReturnUrl)}
                      placeholder="https://example.com/merci"
                    />
                    {errors.cryptoReturnUrl && (
                      <p className="text-xs text-destructive">
                        {errors.cryptoReturnUrl}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {kind === "WITHDRAWAL" && isCrypto && (
                <div className="grid gap-3 rounded-lg border bg-accent/30 p-3">
                  <p className="text-sm font-semibold">Décaissement crypto</p>
                  <p className="text-xs text-muted-foreground">
                    Le montant saisi est débité dans la devise du wallet. Le
                    fournisseur calcule son équivalent crypto au moment du
                    payout.
                  </p>
                  <div className="grid gap-1.5">
                    <Label htmlFor="crypto-memo">
                      Mémo de destination (facultatif)
                    </Label>
                    <Input
                      id="crypto-memo"
                      value={cryptoMemo}
                      onChange={(event) => setCryptoMemo(event.target.value)}
                    />
                  </div>
                </div>
              )}
              {kind === "DEPOSIT" && otpRequired && (
                <div className="grid gap-1.5">
                  <Label htmlFor="payment-otp">Code OTP</Label>
                  <Input
                    id="payment-otp"
                    data-testid="input-payment-otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value)}
                    maxLength={32}
                    required
                    aria-invalid={Boolean(errors.otp)}
                    placeholder="Saisissez le code reçu"
                  />
                  <p className="text-xs text-muted-foreground">
                    Cette méthode exige un code de validation envoyé par votre
                    opérateur.
                  </p>
                  {errors.otp && (
                    <p className="text-xs text-destructive">{errors.otp}</p>
                  )}
                </div>
              )}
              {!(kind === "DEPOSIT" && isCrypto) && (
                <div className="grid gap-1.5">
                  <Label htmlFor="payment-target">{targetLabel}</Label>
                  <Input
                    id="payment-target"
                    data-testid="input-payment-target"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    aria-invalid={Boolean(errors.target)}
                    placeholder={
                      kind === "TRANSFER"
                        ? "BCP26K7M4X2Q9R5P"
                        : kind === "WITHDRAWAL" && isCrypto
                          ? "Adresse blockchain"
                          : `${country?.dial ?? ""} 6XX XX XX XX`
                    }
                  />
                  {errors.target && (
                    <p className="text-xs text-destructive">{errors.target}</p>
                  )}
                </div>
              )}
              <div className="grid gap-1.5">
                <Label htmlFor="payment-description">Libellé (optionnel)</Label>
                <Input
                  id="payment-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Facture 2026-18"
                  maxLength={60}
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={!active.length}
                data-testid="button-payment-review"
              >
                Vérifier
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
